import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { LANGUAGE_NATIVE_NAMES, SUPPORTED_LANGUAGES } from '../../i18n/config';
import { switchLanguage } from '../../i18n/languagePath';
import { useLanguage } from '../../i18n/useLanguage';
import { cn } from '../utils/cn';

const FOCUS =
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900 dark:focus-visible:outline-white';

/**
 * Sélecteur de langue en liste déroulante : le bouton n'affiche que la langue active (icône +
 * code), le menu propose les autres. Radix DropdownMenu fournit le rôle "menu", la navigation
 * au clavier (flèches, Échap), le focus et le positionnement (il se retourne près des bords).
 * Les entrées restent des liens : chaque version de la page a sa propre URL (LanguageLayout
 * change la langue et la mémorise) et les alternates hreflang sont déjà déclarés par <Seo />.
 */
export default function LanguageSwitcher() {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const { pathname, search, hash } = useLocation();

    return (
        <DropdownMenu.Root modal={false}>
            <DropdownMenu.Trigger
                aria-label={t('language.current', { language: LANGUAGE_NATIVE_NAMES[language] })}
                className={cn(
                    'inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium uppercase hover:bg-gray-100 dark:border-gray-700 dark:hover:bg-gray-800',
                    FOCUS,
                )}
            >
                <Languages aria-hidden="true" className="size-4 shrink-0" />
                <span aria-hidden="true">{language}</span>
                <ChevronDown aria-hidden="true" className="size-3.5 shrink-0" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="end"
                    sideOffset={8}
                    collisionPadding={8}
                    className="z-50 min-w-40 rounded-lg border border-gray-200 bg-white p-1 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-900"
                >
                    {SUPPORTED_LANGUAGES.map((lng) => {
                        const isCurrent = lng === language;
                        return (
                            <DropdownMenu.Item key={lng} asChild>
                                <Link
                                    to={`${switchLanguage(pathname, lng)}${search}${hash}`}
                                    lang={lng}
                                    hrefLang={lng}
                                    aria-current={isCurrent ? 'true' : undefined}
                                    className={cn(
                                        'flex cursor-pointer items-center justify-between gap-4 rounded-md px-3 py-2 outline-none data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-800',
                                        isCurrent && 'font-semibold',
                                    )}
                                >
                                    {LANGUAGE_NATIVE_NAMES[lng]}
                                    {isCurrent && (
                                        <Check aria-hidden="true" className="size-4 shrink-0" />
                                    )}
                                </Link>
                            </DropdownMenu.Item>
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
